import React, { useLayoutEffect, useRef } from 'react';
import classNames from 'classnames';

import useScrollDirection from '../../../util/useScrollDirection';

import appSettings from '../../../config/settings';
import { useConfiguration } from '../../../context/configurationContext';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';

import { pickBy } from '../../../util/common';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { isMainSearchTypeKeywords, isOriginInUse } from '../../../util/search';
import { parse, stringify } from '../../../util/urlHelpers';
import { createResourceLocatorString, matchPathname, pathByRouteName } from '../../../util/routes';
import { MIXPANEL_EVENTS, trackMixpanelEvent } from '../../../util/mixpanel';
import {
  Button,
  IconArrowHead,
  LimitedAccessBanner,
  LinkedLogo,
  Modal,
  ModalMissingInformation,
} from '../../../components';
import { getSearchPageResourceLocatorStringParams } from '../../SearchPage/SearchPage.shared';

import TopbarSearchForm from './TopbarSearchForm/TopbarSearchForm';
import TopbarMobileMenu from './TopbarMobileMenu/TopbarMobileMenu';
import TopbarDesktop from './TopbarDesktop/TopbarDesktop';
import MenuIcon from './MenuIcon';
import SearchModal from './SearchModal/SearchModal';

import css from './Topbar.module.css';
import { getCurrentUserTypeRoles, showCreateListingLinkForUser } from '../../../util/userHelpers';

const MAX_MOBILE_SCREEN_WIDTH = 1024;

/**
 * Mobile topbar layout selection.
 *
 * There are two mobile topbar variants:
 *  - 'heroOverlay' (default): a horizontal bar with the logo on the LEFT and the
 *    burger menu on the RIGHT.
 *  - 'vertical': a stacked/column layout (menu label + logo).
 *
 * By default EVERY page uses the 'heroOverlay' (logo + burger) variant on mobile.
 * Only the pages listed below use the VERTICAL mobile topbar.
 *
 * 👉 ADD PAGES THAT SHOULD USE THE VERTICAL MOBILE TOPBAR HERE:
 *    (use the route/page name, e.g. 'LandingPage', or for sub-routes
 *     'InboxPage:orders'. See getResolvedCurrentPage below for naming.)
 */
const VERTICAL_TOPBAR_MOBILE_PAGES = [
  'LandingPage',
  'SearchPage',
];

const MOBILE_MENU_BUTTON_ID = 'mobileMenuButton';
const MOBILE_SEARCH_BUTTON_ID = 'mobileSearchButton';

const redirectToURLWithModalState = (history, location, modalStateParam) => {
  const { pathname, search, state } = location;
  const searchString = `?${stringify({ [modalStateParam]: 'open', ...parse(search) })}`;
  history.push(`${pathname}${searchString}`, state);
};

const redirectToURLWithoutModalState = (history, location, modalStateParam) => {
  const { pathname, search, state } = location;
  const queryParams = pickBy(parse(search), (v, k) => {
    return k !== modalStateParam;
  });
  const stringified = stringify(queryParams);
  const searchString = stringified ? `?${stringified}` : '';
  history.push(`${pathname}${searchString}`, state);
};

const isPrimary = o => o.group === 'primary';
const isSecondary = o => o.group === 'secondary';
const compareGroups = (a, b) => {
  const isAHigherGroupThanB = isPrimary(a) && isSecondary(b);
  const isALesserGroupThanB = isSecondary(a) && isPrimary(b);
  // Note: sort order is stable in JS
  return isAHigherGroupThanB ? -1 : isALesserGroupThanB ? 1 : 0;
};
// Returns links in order where primary links are returned first
const sortCustomLinks = customLinks => {
  const links = Array.isArray(customLinks) ? [...customLinks] : [];
  return links.sort(compareGroups);
};

// Resolves in-app links against route configuration
const getResolvedCustomLinks = (customLinks, routeConfiguration) => {
  const links = Array.isArray(customLinks) ? customLinks : [];
  return links.map(linkConfig => {
    const { type, href } = linkConfig;
    const isInternalLink = type === 'internal' || href.charAt(0) === '/';
    if (isInternalLink) {
      // Internal link
      try {
        const testURL = new URL('http://my.marketplace.com' + href);
        const matchedRoutes = matchPathname(testURL.pathname, routeConfiguration);
        if (matchedRoutes.length > 0) {
          const found = matchedRoutes[0];
          const to = { search: testURL.search, hash: testURL.hash };
          return {
            ...linkConfig,
            route: {
              name: found.route?.name,
              params: found.params,
              to,
            },
          };
        }
      } catch (e) {
        return linkConfig;
      }
    }
    return linkConfig;
  });
};

const isCMSPage = found =>
  found.route?.name === 'CMSPage' ? `CMSPage:${found.params?.pageId}` : null;
const isInboxPage = found =>
  found.route?.name === 'InboxPage' ? `InboxPage:${found.params?.tab}` : null;
// Find the name of the current route/pathname.
// It's used as handle for currentPage check.
const getResolvedCurrentPage = (location, routeConfiguration) => {
  const matchedRoutes = matchPathname(location.pathname, routeConfiguration);
  if (matchedRoutes.length > 0) {
    const found = matchedRoutes[0];
    const cmsPageName = isCMSPage(found);
    const inboxPageName = isInboxPage(found);
    return cmsPageName ? cmsPageName : inboxPageName ? inboxPageName : `${found.route?.name}`;
  }
};

const GenericError = props => {
  const { show } = props;
  const classes = classNames(css.genericError, {
    [css.genericErrorVisible]: show,
  });
  return show ? (
    <div className={classes} role="alert">
      <div className={css.genericErrorContent}>
        <p className={css.genericErrorText}>
          <FormattedMessage id="Topbar.genericError" />
        </p>
      </div>
    </div>
  ) : null;
};

const TopbarComponent = props => {
  const {
    className,
    rootClassName,
    desktopClassName,
    mobileRootClassName,
    mobileClassName,
    mobileVariant,
    isAuthenticated,
    isLoggedInAs,
    authScopes = [],
    authInProgress,
    currentUser,
    currentUserHasListings,
    currentUserListingsCount,
    currentUserHasOrders,
    currentPage,
    notificationCount = 0,
    intl,
    history,
    location,
    onManageDisableScrolling,
    onResendVerificationEmail,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    showGenericError,
    config,
    routeConfiguration,
    // Hide the topbar (both mobile + desktop) when scrolling down and reveal it
    // when scrolling up. Enabled by default; pass hideOnScroll={false} to opt out.
    hideOnScroll = true,
  } = props;
  const [isSearchModalOpen, setIsSearchModalOpen] = React.useState(false);

  const { mobilemenu, mobilesearch, keywords, address, origin, bounds } = parse(location.search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  // Track the mobile/desktop layout reactively so the topbar updates when the
  // viewport crosses the breakpoint (e.g. resizing or rotating the device)
  // WITHOUT needing a page refresh.
  const getIsMobileLayout = () => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return true;
    }
    return window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`).matches;
  };
  const [isMobileLayout, setIsMobileLayout] = React.useState(getIsMobileLayout);

  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return undefined;
    }
    const mql = window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`);
    const handleChange = e => setIsMobileLayout(e.matches);

    // Sync once on mount (covers any SSR/client mismatch).
    setIsMobileLayout(mql.matches);

    // Safari < 14 only supports the deprecated addListener/removeListener API.
    if (mql.addEventListener) {
      mql.addEventListener('change', handleChange);
    } else {
      mql.addListener(handleChange);
    }
    return () => {
      if (mql.removeEventListener) {
        mql.removeEventListener('change', handleChange);
      } else {
        mql.removeListener(handleChange);
      }
    };
  }, []);

  const isMobileMenuOpen = isMobileLayout && mobilemenu === 'open';
  const isMobileSearchOpen = isMobileLayout && mobilesearch === 'open';

  const isTopbarVisibleFromScroll = useScrollDirection(hideOnScroll);
  // Keep topbar interactive while a menu/modal is open. hideOnScroll uses
  // pointer-events: none, which would block the hamburger after page scrolls
  // (e.g. BookingFlowPage auto-scrolls to the cards on mount).
  const isTopbarVisible =
    isTopbarVisibleFromScroll || isMobileMenuOpen || isMobileSearchOpen || isSearchModalOpen;
  const rootRef = useRef(null);

  useLayoutEffect(() => {
    if (!hideOnScroll || !rootRef.current) {
      return undefined;
    }

    const root = rootRef.current;
    root.style.transition = 'transform 0.3s ease';

    if (isTopbarVisible) {
      root.style.transform = '';
      root.classList.remove(css.topbarRootHidden);
    } else {
      // On some pages (e.g. SearchPage) the topbar root is given an overriding
      // rootClassName that collapses its box to 0px height while the visible bar
      // overflows it. In that case `translateY(-100%)` would resolve to 0px and
      // nothing would move, so translate by the measured content height instead.
      const rect = root.getBoundingClientRect();
      const distance = Math.ceil(Math.max(rect.height, root.scrollHeight));
      root.style.transform = distance > 0 ? `translateY(-${distance}px)` : 'translateY(-100%)';
      root.classList.add(css.topbarRootHidden);
    }

    return () => {
      root.classList.remove(css.topbarRootHidden);
      root.style.transform = '';
      root.style.transition = '';
    };
  }, [hideOnScroll, isTopbarVisible]);

  const handleSubmit = values => {
    const { currentSearchParams, history, location, config, routeConfiguration } = props;

    const topbarSearchParams = () => {
      if (isMainSearchTypeKeywords(config)) {
        return { keywords: values?.keywords };
      }
      // topbar search defaults to 'location' search
      const { search, selectedPlace } = values?.location || {};
      const { origin, bounds } = selectedPlace || {};
      const originMaybe = isOriginInUse(config) ? { origin } : {};

      return {
        ...originMaybe,
        address: search,
        bounds,
      };
    };
    const searchParams = {
      ...currentSearchParams,
      ...topbarSearchParams(),
    };

    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );

    history.push(
      createResourceLocatorString(routeName, routeConfiguration, pathParams, searchParams)
    );
  };

  const handleLogout = () => {
    const { onLogout, history, routeConfiguration } = props;
    onLogout().then(() => {
      const path = pathByRouteName('LandingPage', routeConfiguration);

      // In production we ensure that data is really lost,
      // but in development mode we use stored values for debugging
      if (appSettings.dev) {
        history.push(path);
      } else if (typeof window !== 'undefined') {
        window.location = path;
      }

      console.log('logged out'); // eslint-disable-line
    });
  };

  const userType = currentUser?.attributes?.profile?.publicData?.userType;
  const isServiceProvider = userType === 'service_provider' || userType === 'serviceprovider';

  const showManageListingsLink = showCreateListingLinkForUser(config, currentUser);
  let showCreateListingsLink = showManageListingsLink;
  if (
    isServiceProvider &&
    (currentUserListingsCount === null ||
      currentUserListingsCount === undefined ||
      currentUserListingsCount > 0)
  ) {
    showCreateListingsLink = false;
  }


  const { customer: isCustomer, provider: isProvider } = getCurrentUserTypeRoles(
    config,
    currentUser
  );

  /**
   * Determine which tab to use in the inbox link:
   * - if userType is customer – orders
   * - if userType is admin – sales
   * - fallback:
   *   - if only provider role – sales
   *   - if only customer role – orders
   *   - if both roles – determine by currentUserHasListings value
   */
  const topbarInboxTab =
    userType === 'customer'
      ? 'orders'
      : userType === 'admin'
      ? 'sales'
      : !isCustomer
      ? 'sales'
      : !isProvider
      ? 'orders'
      : currentUserHasListings
      ? 'sales'
      : 'orders';

  // Custom links are sorted so that group="primary" are always at the beginning of the list.
  const sortedCustomLinks = sortCustomLinks(config.topbar?.customLinks);
  const customLinks = getResolvedCustomLinks(sortedCustomLinks, routeConfiguration);
  const resolvedCurrentPage = currentPage || getResolvedCurrentPage(location, routeConfiguration);

  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;

  // Default mobile topbar = 'heroOverlay' (logo + burger). Pages listed in
  // VERTICAL_TOPBAR_MOBILE_PAGES use the vertical layout instead. An explicit
  // `mobileVariant` prop always takes precedence.
  const effectiveMobileVariant =
    mobileVariant ||
    (VERTICAL_TOPBAR_MOBILE_PAGES.includes(resolvedCurrentPage) ? 'vertical' : 'heroOverlay');
  // Any variant other than 'heroOverlay' (e.g. 'vertical' / 'default') renders
  // the stacked vertical mobile topbar.
  const isHeroOverlayMobile = isMobileLayout && effectiveMobileVariant === 'heroOverlay';

  const mobileMenu = (
    <TopbarMobileMenu
      isAuthenticated={isAuthenticated}
      currentUser={currentUser}
      onLogout={handleLogout}
      onClose={() => redirectToURLWithoutModalState(history, location, 'mobilemenu')}
      onSearchClick={() => {
        redirectToURLWithoutModalState(history, location, 'mobilemenu');
        setIsSearchModalOpen(true);
      }}
      config={config}
      notificationCount={notificationCount}
      currentPage={resolvedCurrentPage}
      customLinks={customLinks}
      showCreateListingsLink={showCreateListingsLink}
      showManageListingsLink={showManageListingsLink}
      inboxTab={topbarInboxTab}
    />
  );

  const topbarSearcInitialValues = () => {
    if (isMainSearchTypeKeywords(config)) {
      return { keywords };
    }

    // Only render current search if full place object is available in the URL params
    const locationFieldsPresent = isOriginInUse(config)
      ? address && origin && bounds
      : address && bounds;
    return {
      location: locationFieldsPresent
        ? {
            search: address,
            selectedPlace: { address, origin, bounds },
          }
        : null,
    };
  };
  const initialSearchFormValues = topbarSearcInitialValues();

  const classes = classNames(rootClassName || css.root, className);

  const handleSkipToMainContent = e => {
    e.preventDefault();
    const mainContent = document.getElementById('main-content');
    if (mainContent) {
      mainContent.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Focus the main content for screen readers
      mainContent.setAttribute('tabindex', '-1');
      mainContent.focus();
      // Remove tabindex after blur to avoid tabbing into it later
      mainContent.addEventListener(
        'blur',
        () => {
          mainContent.removeAttribute('tabindex');
        },
        { once: true }
      );
    }
  };

  const handleHeaderNavigationClick = event => {
    const link = event.target.closest?.('a');
    if (!link) {
      return;
    }
    trackMixpanelEvent(MIXPANEL_EVENTS.HEADER_NAVIGATION_CLICKED, {
      destination: link.getAttribute('href') || null,
      link_label: link.textContent?.replace(/\s+/g, ' ').trim() || link.getAttribute('aria-label'),
      navigation_variant: window.matchMedia('(max-width: 1023px)').matches
        ? 'mobile'
        : 'desktop',
    });
  };

  return (
    <div className={classes} ref={rootRef} onClick={handleHeaderNavigationClick}>
      <Button onClick={handleSkipToMainContent} className={css.skipToMainContent}>
        <FormattedMessage id="Topbar.skipToMainContent" />
        <IconArrowHead direction="right" size="small" rootClassName={css.skiptoMainArrow} />
      </Button>
      <LimitedAccessBanner
        isAuthenticated={isAuthenticated}
        isLoggedInAs={isLoggedInAs}
        authScopes={authScopes}
        currentUser={currentUser}
        onLogout={handleLogout}
        currentPage={resolvedCurrentPage}
      />
      <nav
        className={classNames(mobileRootClassName || css.container, mobileClassName, {
          [css.containerHeroOverlay]: isHeroOverlayMobile,
        })}
      >
        {isHeroOverlayMobile ? (
          <>
            <LinkedLogo
              id="logo-topbar-mobile"
              className={css.mobileLogoLinkHeroOverlay}
              logoImageClassName={css.heroOverlayLogoImage}
              logoImageMobile="/static/img/logo.svg"
              layout="mobile"
              alt={intl.formatMessage({ id: 'Topbar.logoIcon' })}
              linkToExternalSite={config?.topbar?.logoLink}
            />
            <Button
              id={MOBILE_MENU_BUTTON_ID}
              rootClassName={classNames(css.menuButton, css.menuButtonHeroOverlay)}
              onClick={() => redirectToURLWithModalState(history, location, 'mobilemenu')}
              title={intl.formatMessage({ id: 'Topbar.menuIcon' })}
            >
              <MenuIcon
                rootClassName={css.rootMenuIconHeroOverlay}
                ariaLabel={intl.formatMessage({ id: 'Topbar.menuIcon' })}
              />
              {notificationDot}
            </Button>
          </>
        ) : (
          <>
            <Button
              id={MOBILE_MENU_BUTTON_ID}
              rootClassName={css.menuButton}
              onClick={() => redirectToURLWithModalState(history, location, 'mobilemenu')}
              title={intl.formatMessage({ id: 'Topbar.menuIcon' })}
            >
              <span className={css.menuButtonLabel}>
                <FormattedMessage id="Topbar.menuLabel" />
              </span>
              {notificationDot}
            </Button>
            <LinkedLogo
              id="logo-topbar-mobile"
              className={css.mobileLogoLink}
              layout="mobile"
              alt={intl.formatMessage({ id: 'Topbar.logoIcon' })}
              linkToExternalSite={config?.topbar?.logoLink}
            />
          </>
        )}
      </nav>
      <div className={css.desktop}>
        <TopbarDesktop
          className={desktopClassName}
          currentUser={currentUser}
          currentPage={resolvedCurrentPage}
          intl={intl}
          isAuthenticated={isAuthenticated}
          onLogout={handleLogout}
          config={config}
          showCreateListingsLink={showCreateListingsLink}
          showManageListingsLink={showManageListingsLink}
          inboxTab={topbarInboxTab}
          onSearchClick={() => setIsSearchModalOpen(true)}
        />
      </div>
      <Modal
        id="TopbarMobileMenu"
        containerClassName={css.modalContainer}
        isOpen={isMobileMenuOpen}
        onClose={() => redirectToURLWithoutModalState(history, location, 'mobilemenu')}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
        focusElementId={MOBILE_MENU_BUTTON_ID}
      >
        {authInProgress ? null : mobileMenu}
      </Modal>
      <Modal
        id="TopbarMobileSearch"
        containerClassName={css.modalContainerSearchForm}
        isOpen={isMobileSearchOpen}
        onClose={() => redirectToURLWithoutModalState(history, location, 'mobilesearch')}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
        focusElementId={MOBILE_SEARCH_BUTTON_ID}
      >
        <div className={css.searchContainer}>
          <TopbarSearchForm
            onSubmit={handleSubmit}
            initialValues={initialSearchFormValues}
            isMobile
            appConfig={config}
          />
          <p className={css.mobileHelp}>
            <FormattedMessage id="Topbar.mobileSearchHelp" />
          </p>
        </div>
      </Modal>
      <ModalMissingInformation
        id="MissingInformationReminder"
        containerClassName={css.missingInformationModal}
        currentUser={currentUser}
        currentUserHasListings={currentUserHasListings}
        currentUserHasOrders={currentUserHasOrders}
        location={location}
        onManageDisableScrolling={onManageDisableScrolling}
        onResendVerificationEmail={onResendVerificationEmail}
        sendVerificationEmailInProgress={sendVerificationEmailInProgress}
        sendVerificationEmailError={sendVerificationEmailError}
      />

      <GenericError show={showGenericError} />

      <SearchModal
        isOpen={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
        onManageDisableScrolling={onManageDisableScrolling}
      />
    </div>
  );
};

/**
 * Topbar containing logo, main search and navigation links.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.desktopClassName add more style rules for TopbarDesktop
 * @param {Object} props.mobileRootClassName overwrite mobile layout root classes
 * @param {Object} props.mobileClassName add more style rules for mobile layout
 * @param {('vertical' | 'heroOverlay' | 'default')?} props.mobileVariant mobile topbar layout variant ('vertical'/'default' = stacked; 'heroOverlay' = logo + burger)
 * @param {boolean} props.isAuthenticated
 * @param {boolean} props.isLoggedInAs
 * @param {Object} props.currentUser
 * @param {boolean} props.currentUserHasListings
 * @param {boolean} props.currentUserHasOrders
 * @param {string} props.currentPage
 * @param {number} props.notificationCount
 * @param {Function} props.onLogout
 * @param {Function} props.onManageDisableScrolling
 * @param {Function} props.onResendVerificationEmail
 * @param {Object} props.sendVerificationEmailInProgress
 * @param {Object} props.sendVerificationEmailError
 * @param {boolean} props.showGenericError
 * @param {Object} props.history
 * @param {Function} props.history.push
 * @param {Object} props.location
 * @param {string} props.location.search '?foo=bar'
 * @param {boolean} props.hideOnScroll - Hide topbar on scroll down, show on scroll up
 * @returns {JSX.Element} topbar component
 */
const Topbar = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  return (
    <TopbarComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      {...props}
    />
  );
};

export default Topbar;
